import axios, { AxiosResponse } from "axios";
import { environment } from "../../environment";
import { useLogin } from "../login/login";
import { ICrmError } from "./types";

const crmUrl = `${environment.v2Url}/crm`;

// #region types
/** One published Shopify product of a design (wallpaper, gift wrap, ...). */
export interface ICrmDesignListing {
    product_type: string;
    shopify_product_id: string;
}

/** One design on the lead's brand, with its numbers over the chosen window. */
export interface ICrmLeadDesign {
    id: number;
    title: string | null;
    status: string | null;
    created_at: string;
    image_url: string | null;
    collection: string | null;
    catalog_published: boolean;
    shopify_product_id: string | null;
    listings: ICrmDesignListing[];
    /** Retail sales from orders with this design, split by line cost across the order's designs. */
    sales: number;
    /** Customer orders with a non-sample line of this design. */
    orders: number;
    /** Sample lines of this design. */
    samples: number;
    last_ordered_at: string | null;
}

/** The window the numbers cover: from null = all time, to exclusive. */
export interface ICrmDesignWindow {
    from: string | null;
    to: string;
}

export interface ICrmLeadDesigns {
    brand_id: number | null;
    store: string | null;
    window: ICrmDesignWindow;
    designs: ICrmLeadDesign[];
    totals: { designs: number; sales: number; orders: number; samples: number };
}

export interface ICrmDesignOrderShare {
    order_id: number;
    order_number: string | null;
    at: string;
    retail: boolean;
    sale: number;
    samples: number;
    units: number;
}

export interface ICrmLeadDesignDetail {
    design_id: number;
    window: ICrmDesignWindow;
    sales: number;
    orders: number;
    samples: number;
    units: number;
    series: { unit: 'day' | 'week' | 'month'; starts: string[]; sales: number[]; samples: number[] };
    recent: ICrmDesignOrderShare[];
}

/** A from–to pair as ISO times; omit both for all time. */
export type CrmDesignRangeQuery = { from?: string; to?: string };

export type CrmDesignsKeyFunctions = {
    getDesigns: (leadId: number, range: CrmDesignRangeQuery) => Promise<ICrmLeadDesigns | ICrmError>;
    getDesign: (leadId: number, designId: number, range: CrmDesignRangeQuery) => Promise<ICrmLeadDesignDetail | ICrmError>;
};

// #region useCrmDesigns
/** The lead page's Designs tab. Never throws: each call resolves the payload or an ICrmError. */
export const useCrmDesigns = (): [CrmDesignsKeyFunctions] => {
    const [, authToken, loginFunctions] = useLogin();
    const headers = { 'X-Wallmates-Auth': authToken };

    const handle = (err: any): ICrmError => {
        const status = err?.response?.status ?? 0;
        if (status === 401) loginFunctions.logout();
        return {
            ok: false,
            status,
            message: err?.response?.data?.message
                || (status === 0 ? 'Could not reach Wallmates. Check your connection.' : 'Something went wrong.')
        };
    };

    const getDesigns = async (leadId: number, range: CrmDesignRangeQuery): Promise<ICrmLeadDesigns | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmLeadDesigns> = await axios.get(`${crmUrl}/leads/${leadId}/designs`, { headers, params: range });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    const getDesign = async (leadId: number, designId: number, range: CrmDesignRangeQuery): Promise<ICrmLeadDesignDetail | ICrmError> => {
        try {
            const result: AxiosResponse<ICrmLeadDesignDetail> = await axios.get(`${crmUrl}/leads/${leadId}/designs/${designId}`, { headers, params: range });
            return result.data;
        } catch (err: any) { return handle(err); }
    };

    return [{ getDesigns, getDesign }];
};
